import { useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";
import {
  getListDocumentsQueryKey,
  useListDocuments,
} from "~/api/default/default";
import { useGetVolumes } from "~/api/folders/folders";
import { DocumentStatus } from "~/api/model";
import { FolderChatStatus } from "~/api/model/folderChatStatus.ts";
import { getGetProjectQueryKey } from "~/api/projects/projects";
import { Button } from "~/components/ui/button";
import { Label } from "~/components/ui/label";
import { Modal } from "~/components/ui/modal";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { ROUTES, useProjectParams } from "~/helpers";
import { useToast } from "~/hooks/use-toast";
import { useWebSocketAddToVersion } from "~/hooks/useWebSocketAddToVersion";
import { AddToVersionResponse } from "~/lib/wss-client";
import { trackEvent } from "~/utils/analytics";
import { isDocumentBatesInFlight } from "../hooks/useBatesActions";
import { useDocumentSelectionStore } from "../stores/documentSelectionStore";

interface AddToVersionDialogProps {
  open: boolean;
  setOpen: (open: boolean) => void;
}

export const AddToVersionModal: FC<AddToVersionDialogProps> = ({
  open,
  setOpen,
}) => {
  const { organizationId, caseId } = useProjectParams();
  const { selectedDocuments } = useDocumentSelectionStore();
  const { data: volumes } = useGetVolumes(organizationId, caseId);
  const { data: caseDocuments } = useListDocuments(organizationId, caseId);

  const navigate = useNavigate();

  const intl = useIntl();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const versionOptions = useMemo(
    () =>
      volumes
        ?.filter((version) => version.chatStatus === FolderChatStatus.COMPLETE)
        .filter((version) => version.id !== caseId)
        .reduce(
          (acc, version) => {
            if (version.parentId !== null) {
              acc.push({ id: version.id, label: version.name });
            }
            return acc;
          },
          [] as { id: string; label: string }[]
        )
        .sort((a, b) => {
          const versionA = volumes?.find((v) => v.id === a.id);
          const versionB = volumes?.find((v) => v.id === b.id);

          return (
            new Date(versionB?.createdAt || 0).getTime() -
            new Date(versionA?.createdAt || 0).getTime()
          );
        }) || [],
    [volumes]
  );

  const [selectedVersionId, setSelectedVersionId] = useState<string>(
    versionOptions[0]?.id || ""
  );
  const { data: selectedVersionDocuments, isLoading } = useListDocuments(
    organizationId,
    selectedVersionId
  );
  const [duplicateDocuments, setDuplicateDocuments] = useState<
    { id: string; filename: string }[]
  >([]);

  const selectedDocumentIds = useMemo(
    () => selectedDocuments.map((doc) => doc.id),
    [selectedDocuments]
  );

  // A document copied into a version gets its own id, so the upload it came
  // from is the only stable link back to the document selected here.
  const versionUploadIds = useMemo(
    () =>
      new Set(
        (selectedVersionDocuments?.documents ?? [])
          .map((doc) => doc.uploadId)
          .filter((uploadId): uploadId is string => Boolean(uploadId))
      ),
    [selectedVersionDocuments]
  );

  const selectedVersionName = useMemo(
    () =>
      versionOptions.find((option) => option.id === selectedVersionId)?.label,
    [versionOptions, selectedVersionId]
  );

  const handleAddToVersionResponse = useCallback(
    (response: AddToVersionResponse) => {
      if (response.action === "message-error") {
        toast({
          title: intl.formatMessage({
            id: "error",
            defaultMessage: "Error",
          }),
          description:
            response.error ||
            intl.formatMessage({
              id: "addToVersion.error",
              defaultMessage:
                "Failed to add files to version. Please try again.",
            }),
          variant: "destructive",
        });
        return;
      }

      queryClient.invalidateQueries({
        queryKey: getListDocumentsQueryKey(organizationId, selectedVersionId),
      });
      queryClient.invalidateQueries({
        queryKey: getGetProjectQueryKey(organizationId, selectedVersionId),
      });

      toast({
        title: intl.formatMessage({
          id: "addToVersion.ready.title",
          defaultMessage: "Version ready",
        }),
        description: intl.formatMessage(
          {
            id: "addToVersion.ready.description",
            defaultMessage:
              'Files were added to "{versionName}". Redirecting...',
          },
          { versionName: selectedVersionName ?? "" }
        ),
      });

      setOpen(false);

      navigate(
        ROUTES.CASE({
          organizationId,
          caseId: selectedVersionId,
        })
      );
    },
    [
      toast,
      intl,
      queryClient,
      organizationId,
      selectedVersionId,
      selectedVersionName,
      setOpen,
      navigate,
    ]
  );

  const {
    addToVersion,
    isConnected: isWebSocketConnected,
    isLoading: isSending,
  } = useWebSocketAddToVersion({
    onAddToVersionResponse: handleAddToVersionResponse,
  });

  // A document whose Bates state is still in flux (being stamped or unstamped)
  // must not be copied into another version mid-operation, or the copy would
  // be missing or partially carrying its overlays. Block the action until every
  // selected document settles.
  const hasBatesInFlightSelectedDocuments = useMemo(
    () =>
      (caseDocuments?.documents ?? []).some(
        (doc) =>
          selectedDocumentIds.includes(doc.id) &&
          doc.status === DocumentStatus.COMPLETE &&
          isDocumentBatesInFlight(doc)
      ),
    [caseDocuments, selectedDocumentIds]
  );

  // What would actually be sent: the selection minus the files the version
  // already holds and minus the ones whose Bates state is still in flux. The
  // version can hold several copies of the same file, so counting duplicates
  // instead would not tell whether anything is left to send.
  const documentIdsToSend = useMemo(
    () =>
      selectedDocuments
        .filter((doc) => !(doc.uploadId && versionUploadIds.has(doc.uploadId)))
        .filter((doc) => {
          const sourceDocument = caseDocuments?.documents?.find(
            (caseDocument) => caseDocument.id === doc.id
          );
          return !(sourceDocument && isDocumentBatesInFlight(sourceDocument));
        })
        .map((doc) => doc.id),
    [selectedDocuments, versionUploadIds, caseDocuments]
  );

  useEffect(() => {
    if (!open) {
      setSelectedVersionId(versionOptions[0]?.id || "");
      setDuplicateDocuments([]);
      return;
    }
    setSelectedVersionId((current) =>
      versionOptions.some((option) => option.id === current)
        ? current
        : versionOptions[0]?.id || ""
    );
  }, [open, versionOptions]);

  const submit = async () => {
    trackEvent({
      eventName: "app/add_to_version_modal/send_files.button/submit",
    });

    if (!selectedVersionId) {
      return;
    }

    if (hasBatesInFlightSelectedDocuments) {
      return;
    }

    if (documentIdsToSend.length === 0) {
      return;
    }

    if (!caseId) {
      return;
    }

    if (!isWebSocketConnected) {
      toast({
        title: intl.formatMessage({
          id: "error",
          defaultMessage: "Error",
        }),
        description: intl.formatMessage({
          id: "addToVersion.error",
          defaultMessage: "Failed to add files to version. Please try again.",
        }),
        variant: "destructive",
      });
      return;
    }

    addToVersion(caseId, selectedVersionId, documentIdsToSend);
  };

  const handleSendFiles = async () => {
    // Refetch documents list
    await queryClient.invalidateQueries({
      queryKey: getListDocumentsQueryKey(organizationId, selectedVersionId),
    });

    const duplicates =
      selectedVersionDocuments?.documents?.filter((doc) =>
        selectedDocuments.some(
          (selected) => selected.uploadId && selected.uploadId === doc.uploadId
        )
      ) || [];

    if (duplicates.length > 0) {
      setDuplicateDocuments(duplicates);
      return;
    }

    await submit();
  };
  const handleVersionChange = (versionId: string) => {
    setSelectedVersionId(versionId);
    setDuplicateDocuments([]);
  };

  const getModalTitle = () => {
    if (isSending) {
      return (
        <FormattedMessage
          id="addToVersion.title.adding"
          defaultMessage="Adding to Version..."
        />
      );
    }
    if (duplicateDocuments.length > 0) {
      return (
        <FormattedMessage
          id="addToVersion.duplicateFiles.titleDetected"
          defaultMessage="Duplicate Detected"
        />
      );
    }
    return (
      <FormattedMessage id="addToVersion" defaultMessage="Add to Version" />
    );
  };

  const getModalDescription = () => {
    if (isSending) {
      return (
        <FormattedMessage
          id="addToVersion.description.adding"
          defaultMessage='Files are being added to "<b>{versionName}</b>". You&apos;ll be redirected when ready.'
          values={{
            versionName: selectedVersionName ?? "",
            b: (chunks) => (
              <strong key="versionName" className="font-semibold">
                {chunks}
              </strong>
            ),
          }}
        />
      );
    }
    if (duplicateDocuments.length > 0) {
      return (
        <FormattedMessage
          id="addToVersion.duplicateFiles.existingFiles"
          defaultMessage="This already exists:"
        />
      );
    }
    return (
      <FormattedMessage
        id="addToVersion.hint"
        defaultMessage="Select the version to which you want to send the selected files."
      />
    );
  };

  return (
    <Modal
      open={open}
      closeDisabled={isSending}
      onOpenChange={(newOpen) => {
        if (!isSending) {
          setOpen(newOpen);
        }
      }}
      title={getModalTitle()}
      description={getModalDescription()}
      content={
        isSending ? (
          <div className="flex flex-col items-center justify-center gap-4 py-8">
            <Loader2 className="text-primary h-8 w-8 animate-spin" />
            <p className="text-muted-foreground text-sm">
              <FormattedMessage
                id="versionCreation.status.pleaseWait"
                defaultMessage="Please wait..."
              />
            </p>
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            {duplicateDocuments.length > 0 ? (
              <ul className="list-disc pl-4 text-sm">
                {duplicateDocuments.map((doc) => (
                  <li key={doc.id}>{doc.filename}</li>
                ))}
              </ul>
            ) : (
              <div className="flex flex-col gap-2">
                <Label>
                  <FormattedMessage
                    id="addToVersion.selectVersion"
                    defaultMessage="Select version"
                  />
                </Label>
                <Select
                  onValueChange={handleVersionChange}
                  value={selectedVersionId}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue
                      placeholder={intl.formatMessage({
                        id: "selectVersionPlaceholder",
                        defaultMessage: "Select a version",
                      })}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      {versionOptions.map((option) => (
                        <React.Fragment key={option.id}>
                          <SelectItem value={option.id}>
                            {option.label}
                          </SelectItem>
                        </React.Fragment>
                      ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </div>
            )}
            {duplicateDocuments.length > 0 && documentIdsToSend.length > 0 && (
              <p className="text-muted-foreground text-sm font-normal ">
                <FormattedMessage
                  id="addToVersion.duplicateFiles.skipMessage"
                  defaultMessage="Existing files will be skipped."
                />
              </p>
            )}
            {hasBatesInFlightSelectedDocuments && (
              <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
                <p className="text-amber-800 text-sm">
                  <FormattedMessage
                    id="addToVersion.stampingInProgress"
                    defaultMessage="Some documents are still updating their reference numbers. Wait until that finishes to add them to a version."
                  />
                </p>
              </div>
            )}
          </div>
        )
      }
      footer={
        isSending ? null : (
          <>
            <Button
              variant="outline"
              disabled={isLoading}
              onClick={() => {
                trackEvent({
                  eventName: "app/add_to_version_modal/cancel.button/click",
                });
                setOpen(false);
              }}
            >
              <FormattedMessage id="cancel" defaultMessage="Cancel" />
            </Button>
            {duplicateDocuments.length > 0 ? (
              documentIdsToSend.length > 0 && (
                <Button
                  disabled={
                    isLoading ||
                    !isWebSocketConnected ||
                    !selectedVersionId ||
                    !caseId ||
                    hasBatesInFlightSelectedDocuments
                  }
                  onClick={submit}
                >
                  {!isWebSocketConnected && (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  )}
                  <FormattedMessage id="continue" defaultMessage="Continue" />
                </Button>
              )
            ) : (
              <Button
                disabled={
                  isLoading ||
                  !isWebSocketConnected ||
                  !selectedVersionId ||
                  !caseId ||
                  hasBatesInFlightSelectedDocuments
                }
                onClick={handleSendFiles}
              >
                {!isWebSocketConnected && (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                )}
                <FormattedMessage
                  id="addToVersion.sendFiles"
                  defaultMessage="Send Files"
                />
              </Button>
            )}
          </>
        )
      }
    />
  );
};
